import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeactivateAdminUser, type AdminUser } from "@/queries/adminUsers";

import { classifyApiError } from "./errors";

interface DeactivateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser;
}

export function DeactivateUserDialog({
  open,
  onOpenChange,
  user,
}: DeactivateUserDialogProps) {
  const { t } = useTranslation("admin-users");
  const deactivate = useDeactivateAdminUser(user.id);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setErrorKey(null);
      deactivate.reset();
    }
    onOpenChange(next);
  };

  const onConfirm = () => {
    setErrorKey(null);
    deactivate.mutate(undefined, {
      onSuccess: () => handleOpenChange(false),
      onError: (err) => {
        const bucket = classifyApiError(err);
        if (bucket === "deactivateSelf" || bucket === "self") setErrorKey("self");
        else if (bucket === "lastAdmin") setErrorKey("lastAdmin");
        else setErrorKey("generic");
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("deactivate.title")}</DialogTitle>
          <DialogDescription>
            {t("deactivate.description", { email: user.email })}
          </DialogDescription>
        </DialogHeader>

        {errorKey ? (
          <Alert variant="destructive">
            <AlertDescription>{t(`deactivate.errors.${errorKey}`)}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={deactivate.isPending}
          >
            {t("deactivate.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={deactivate.isPending}
          >
            {deactivate.isPending
              ? t("deactivate.submitting")
              : t("deactivate.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
